import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useJoinRuntime } from "../../runtime/hooks/useJoinRuntime";
import { useOrchestrator } from "./contexts/OrchestratorContext";
import "./MultiPlayer.css";

const JoinEntryPoint: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ hostId?: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlHostId = queryParams.get("url") || params.hostId;

  const { setOrchestrator } = useOrchestrator();
  const { status, progress, connect, cancel } = useJoinRuntime();

  const [savedPlayerName] = useLocalStorage("playerName", "");
  const [playerName, setPlayerName] = useState<string>(
    (savedPlayerName as string) || ""
  );
  const [hostId, setHostId] = useState<string>(urlHostId || "");
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Prevent double-join on strict mode
  const joinAttempted = useRef(false);

  const handleConnect = async (targetId: string, name: string) => {
      if (joinAttempted.current) return;
      joinAttempted.current = true;

      setIsSubmitting(true);
      try {
        const orchestrator = await connect({
          hostId: targetId.trim(),
          playerName: name.trim() || "QuadraPlayer",
          team: 0,
          // If hostId is a URL (contains ://), treat it as wsUrl
          wsUrl: targetId.trim().includes("://") ? targetId.trim() : undefined
        });
        setOrchestrator(orchestrator);
        navigate("/multi-player/game");
      } catch (err: any) {
        console.error("Join Error:", err);
        setError(err?.message || "Failed to join game");
        setIsSubmitting(false);
        joinAttempted.current = false;
      }
  };

  // Auto-join if URL is provided
  useEffect(() => {
      if (urlHostId && !joinAttempted.current) {
          handleConnect(urlHostId, playerName || "QuadraPlayer");
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlHostId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    if (!hostId.trim()) {
      setError("Host ID required");
      return;
    }

    // Reset ref for manual attempts
    joinAttempted.current = false;
    handleConnect(hostId, playerName);
  };

  const handleCancel = () => {
    cancel();
    setOrchestrator(null);
    setIsSubmitting(false);
    joinAttempted.current = false;
  };

  const statusText = () => {
    switch (status.type) {
      case "connecting":
        return "Connecting to host…";
      case "awaiting-acceptance":
        return "Waiting for host approval…";
      case "downloading":
        return "Downloading game state…";
      case "error":
        return status.message;
      default:
        return "";
    }
  };

  return (
    <div className="multiplayer-setup-wrapper">
      <button className="back-button" onClick={() => navigate("/multi-player")}>
        <span>← Back</span>
      </button>
      <div className="multiplayer-setup-content">
        <form className="multiplayer-setup-container" onSubmit={handleSubmit}>
          <h2>Join Multiplayer Game</h2>

          <div className="input-group">
            <label htmlFor="hostId">Host ID</label>
            <input
              id="hostId"
              type="text"
              value={hostId}
              onChange={(e) => setHostId(e.target.value)}
              placeholder="Enter host ID or URL"
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="input-group">
            <label htmlFor="playerName">Player Name</label>
            <input
              id="playerName"
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Enter your name"
              disabled={isSubmitting}
            />
          </div>

          {statusText() && (
            <div className="status-banner">
              <span>{statusText()}</span>
            </div>
          )}

          {status.type === "downloading" && (
            <div className="progress-bar">
              <div
                className="progress-bar-fill"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="action-buttons">
            <button
              type="submit"
              className="primary-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Joining…" : "Join Game"}
            </button>
            {isSubmitting && (
              <button
                type="button"
                className="secondary-button"
                onClick={handleCancel}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default JoinEntryPoint;